import React, { useState } from 'react';
import { flexRender, Header, Row, Table } from '@tanstack/react-table';
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronDown, ChevronRight, ChevronsLeft, ChevronsRight, ChevronLeft, EyeOff, Filter, Layers, Loader2, MoveHorizontal, Pin, PinOff } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../../ui/dropdown-menu';
import { cn } from '../../../lib/utils';
import type { CatalogueRow } from '../../../lib/catalogueAdminApi';
import { count } from './format';

interface Props {
  table: Table<CatalogueRow>;
  density: 'comfortable' | 'compact';
  loading: boolean;
  onFilterColumn: (id: string) => void;
  /** Called when a style row expands and its SKUs are not loaded yet. */
  onExpandStyle: (styleCode: string) => void;
  loadingStyles: Set<string>;
}

const pinnedStyle = (column: Header<CatalogueRow, unknown>['column']): React.CSSProperties => {
  const pinned = column.getIsPinned();
  if (!pinned) return {};
  return { position: 'sticky', left: pinned === 'left' ? column.getStart('left') : undefined, zIndex: 2 };
};

interface HeaderCellProps {
  header: Header<CatalogueRow, unknown>;
  onFilterColumn: (id: string) => void;
  drag: { id: string | null; over: string | null };
  onDragStart: (id: string) => void;
  onDragOver: (id: string) => void;
  onDrop: (id: string) => void;
  onDragEnd: () => void;
}

const HeaderCell: React.FC<HeaderCellProps> = ({ header, onFilterColumn, drag, onDragStart, onDragOver, onDrop, onDragEnd }) => {
  const col = header.column;
  const canSort = col.getCanSort();
  const sorted = col.getIsSorted();
  const align = col.columnDef.meta?.align;
  const pinned = col.getIsPinned() === 'left';
  const isLastPinned = pinned && col.getIsLastColumn('left');
  const draggable = !pinned && col.id !== 'select';
  const filtered = col.getIsFiltered();
  // "Filter…" opens the chip's popover in the filter bar; the menu must not
  // hand focus back to this header on close, or that popover shuts at once.
  const toFilter = React.useRef(false);
  const content = header.isPlaceholder ? null : flexRender(col.columnDef.header, header.getContext());

  return (
    <th
      style={{ width: header.getSize(), ...pinnedStyle(col) }}
      draggable={draggable}
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = 'move';
        onDragStart(col.id);
      }}
      onDragOver={(e) => {
        if (!drag.id || drag.id === col.id || !draggable) return;
        e.preventDefault();
        onDragOver(col.id);
      }}
      onDrop={(e) => {
        e.preventDefault();
        if (draggable) onDrop(col.id);
      }}
      onDragEnd={onDragEnd}
      className={cn(
        'group relative h-9 border-b border-r border-[color:var(--oa-line-2)] border-r-[color:var(--oa-line-2)] bg-[color:var(--oa-raised)] px-2.5 text-left text-[11px] font-semibold uppercase tracking-[0.05em] text-[color:var(--oa-muted)] select-none',
        isLastPinned && 'shadow-[inset_-1px_0_0_var(--oa-line-2)]',
        drag.id === col.id && 'opacity-40',
        drag.over === col.id && 'shadow-[inset_2px_0_0_var(--oa-accent)]'
      )}
    >
      {col.id === 'select' ? (
        <div className="flex h-full items-center">{content}</div>
      ) : (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className={cn(
                'flex h-full w-full items-center gap-1 rounded text-left outline-none transition-colors hover:text-[color:var(--oa-text)] focus-visible:ring-2 focus-visible:ring-[color:color-mix(in_oklab,var(--oa-accent)_40%,transparent)]',
                align === 'right' && 'flex-row-reverse text-right',
                align === 'center' && 'text-center',
                draggable && 'cursor-grab active:cursor-grabbing'
              )}
            >
              <span className="min-w-0 flex-1 truncate">{content}</span>
              {filtered ? <Filter className="h-3 w-3 text-[color:var(--oa-accent)]" /> : null}
              {canSort ? (
                sorted === 'asc' ? <ArrowUp className="h-3.5 w-3.5 text-[color:var(--oa-accent)]" />
                : sorted === 'desc' ? <ArrowDown className="h-3.5 w-3.5 text-[color:var(--oa-accent)]" />
                : <ArrowUpDown className="h-3.5 w-3.5 opacity-0 transition-opacity group-hover:opacity-50" />
              ) : null}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align={align === 'right' ? 'end' : 'start'}
            className="w-48 rounded-xl"
            onCloseAutoFocus={(e) => {
              if (!toFilter.current) return;
              e.preventDefault();
              toFilter.current = false;
              onFilterColumn(col.id);
            }}
          >
            {canSort ? (
              <>
                <DropdownMenuItem onSelect={() => col.toggleSorting(false)} className="gap-2"><ArrowUp className="h-3.5 w-3.5" /> Sort ascending</DropdownMenuItem>
                <DropdownMenuItem onSelect={() => col.toggleSorting(true)} className="gap-2"><ArrowDown className="h-3.5 w-3.5" /> Sort descending</DropdownMenuItem>
                {sorted ? <DropdownMenuItem onSelect={() => col.clearSorting()} className="gap-2 text-[color:var(--oa-muted)]"><ArrowUpDown className="h-3.5 w-3.5" /> Clear sort</DropdownMenuItem> : null}
                <DropdownMenuSeparator />
              </>
            ) : null}
            {col.columnDef.meta?.filter ? (
              <DropdownMenuItem onSelect={() => { toFilter.current = true; }} className="gap-2"><Filter className="h-3.5 w-3.5" /> Filter…</DropdownMenuItem>
            ) : null}
            {col.columnDef.meta?.groupable ? (
              <DropdownMenuItem onSelect={() => col.toggleGrouping()} className="gap-2"><Layers className="h-3.5 w-3.5" /> {col.getIsGrouped() ? 'Ungroup' : 'Group by this'}</DropdownMenuItem>
            ) : null}
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => col.pin(col.getIsPinned() ? false : 'left')} className="gap-2">
              {col.getIsPinned() ? <><PinOff className="h-3.5 w-3.5" /> Unpin</> : <><Pin className="h-3.5 w-3.5" /> Pin to left</>}
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => col.resetSize()} className="gap-2"><MoveHorizontal className="h-3.5 w-3.5" /> Reset width</DropdownMenuItem>
            {col.getCanHide() ? (
              <DropdownMenuItem onSelect={() => col.toggleVisibility(false)} className="gap-2 text-[color:var(--oa-muted)]"><EyeOff className="h-3.5 w-3.5" /> Hide column</DropdownMenuItem>
            ) : null}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      {col.getCanResize() ? (
        <div
          onMouseDown={header.getResizeHandler()}
          onTouchStart={header.getResizeHandler()}
          onClick={(e) => e.stopPropagation()}
          draggable={false}
          className={cn(
            'absolute right-0 top-1.5 bottom-1.5 w-1 cursor-col-resize rounded-full opacity-0 transition-opacity group-hover:opacity-100 hover:bg-[color:var(--oa-accent)]',
            col.getIsResizing() && 'bg-[color:var(--oa-accent)] opacity-100'
          )}
        />
      ) : null}
    </th>
  );
};

const SkeletonRows: React.FC<{ cols: number; rows: number; h: string }> = ({ cols, rows, h }) => (
  <>
    {Array.from({ length: rows }).map((_, i) => (
      <tr key={i} className={h}>
        {Array.from({ length: cols }).map((__, j) => (
          <td key={j} className="border-b border-[color:var(--oa-line-2)] px-2.5">
            <div className="h-3 rounded bg-[color:var(--oa-raised-2)] animate-pulse" style={{ width: `${40 + ((i * 7 + j * 13) % 50)}%` }} />
          </td>
        ))}
      </tr>
    ))}
  </>
);

// Group header: one full-width band with the column label and key.
const GroupRow: React.FC<{ row: Row<CatalogueRow>; table: Table<CatalogueRow>; leafCount: number; rowH: string }> = ({ row, table, leafCount, rowH }) => {
  const key = String(row.getGroupingValue(row.groupingColumnId as string) ?? '—');
  const label = table.getColumn(row.groupingColumnId as string)?.columnDef.meta?.label;
  return (
    <tr onClick={() => row.toggleExpanded()} className={cn(rowH, 'cursor-pointer bg-[color:var(--oa-raised)] hover:bg-[color:var(--oa-raised-2)]')}>
      <td colSpan={leafCount} className="border-b border-[color:var(--oa-line-2)] p-0">
        <div className="sticky left-0 flex w-fit max-w-[100vw] items-center gap-2 px-3 text-[13px]" style={{ paddingLeft: 12 + row.depth * 20 }}>
          {row.getIsExpanded() ? <ChevronDown className="h-4 w-4 text-[color:var(--oa-muted)]" /> : <ChevronRight className="h-4 w-4 text-[color:var(--oa-muted)]" />}
          {label ? <span className="text-[11px] font-semibold uppercase tracking-wide text-[color:var(--oa-muted)]">{label}</span> : null}
          <span className="font-semibold text-[color:var(--oa-text)]">{key}</span>
        </div>
      </td>
    </tr>
  );
};

export const CatalogueTable: React.FC<Props> = ({ table, density, loading, onFilterColumn, onExpandStyle, loadingStyles }) => {
  const [drag, setDrag] = useState<{ id: string | null; over: string | null }>({ id: null, over: null });
  const rowH = density === 'compact' ? 'h-9' : 'h-12';
  const rows = table.getRowModel().rows;
  const leafCount = table.getVisibleLeafColumns().length;
  const total = table.getFilteredRowModel().rows.filter((r) => r.original.kind === 'style').length;
  const { pageIndex, pageSize } = table.getState().pagination;
  const from = total === 0 ? 0 : pageIndex * pageSize + 1;
  const to = Math.min(total, (pageIndex + 1) * pageSize);
  const selectedCount = table.getSelectedRowModel().rows.filter((r) => r.original.kind === 'style').length;

  const dropOn = (targetId: string) => {
    const from = drag.id;
    if (!from || from === targetId) return setDrag({ id: null, over: null });
    const order = table.getAllLeafColumns().map((c) => c.id);
    const next = order.filter((id) => id !== from);
    next.splice(next.indexOf(targetId), 0, from);
    table.setColumnOrder(next);
    setDrag({ id: null, over: null });
  };

  const toggleStyle = (row: Row<CatalogueRow>) => {
    const r = row.original;
    if (r.kind !== 'style') return;
    if (!row.getIsExpanded() && !r.variants) onExpandStyle(r.style_code);
    row.toggleExpanded();
  };

  return (
    <>
      <div className="min-h-0 flex-1 overflow-auto bg-[color:var(--oa-card)]">
        <table className="border-separate border-spacing-0 text-[13px] text-[color:var(--oa-text)]" style={{ width: table.getTotalSize(), minWidth: '100%' }}>
          <thead className="sticky top-0 z-[3]">
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id}>
                {hg.headers.map((header) => (
                  <HeaderCell
                    key={header.id}
                    header={header}
                    onFilterColumn={onFilterColumn}
                    drag={drag}
                    onDragStart={(id) => setDrag({ id, over: null })}
                    onDragOver={(id) => setDrag((d) => (d.over === id ? d : { ...d, over: id }))}
                    onDrop={dropOn}
                    onDragEnd={() => setDrag({ id: null, over: null })}
                  />
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {loading ? (
              <SkeletonRows cols={leafCount} rows={20} h={rowH} />
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={leafCount} className="py-24 text-center">
                  <div className="font-montserrat text-[15px] font-extrabold text-[color:var(--oa-text)]">No products match</div>
                  <div className="mt-1 text-[13px] text-[color:var(--oa-muted)]">Loosen a filter or clear the search to see more.</div>
                </td>
              </tr>
            ) : (
              rows.map((row) => {
                if (row.getIsGrouped()) return <GroupRow key={row.id} row={row} table={table} leafCount={leafCount} rowH={rowH} />;
                const r = row.original;
                const isVariant = r.kind === 'variant';
                const selected = row.getIsSelected();
                const open = !isVariant && row.getIsExpanded();
                const pending = open && r.kind === 'style' && !r.variants && loadingStyles.has(r.style_code);
                const bg = isVariant
                  ? 'bg-[color:var(--oa-raised)]'
                  : selected ? 'bg-[color:var(--oa-accent-soft)]' : open ? 'bg-[color:var(--oa-raised)]' : 'bg-[color:var(--oa-card)] group-hover/row:bg-[color:var(--oa-accent-soft)]';
                return (
                  <React.Fragment key={row.id}>
                    <tr
                      onClick={() => toggleStyle(row)}
                      className={cn(rowH, 'group/row', !isVariant && 'cursor-pointer')}
                    >
                      {row.getVisibleCells().map((cell) => {
                        const col = cell.column;
                        const isLastPinned = col.getIsPinned() === 'left' && col.getIsLastColumn('left');
                        const content = cell.getIsPlaceholder() ? null : flexRender(col.columnDef.cell, cell.getContext());
                        return (
                          <td
                            key={cell.id}
                            style={{ width: col.getSize(), ...pinnedStyle(col) }}
                            className={cn('border-b border-r border-[color:var(--oa-line-2)] px-2.5 align-middle', bg, isLastPinned && 'shadow-[inset_-1px_0_0_var(--oa-line-2)]')}
                          >
                            {content}
                          </td>
                        );
                      })}
                    </tr>
                    {pending ? (
                      <tr className="h-8">
                        <td colSpan={leafCount} className="border-b border-[color:var(--oa-line-2)] bg-[color:var(--oa-raised)] px-2.5">
                          <div className="sticky left-0 flex w-fit items-center gap-2 pl-[36px] text-[12px] text-[color:var(--oa-muted)]">
                            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading SKUs…
                          </div>
                        </td>
                      </tr>
                    ) : null}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[color:var(--oa-line-2)] bg-[color:var(--oa-card)] px-4 py-1.5 text-[12px] text-[color:var(--oa-muted)]">
        <div className="tabular-nums">
          {loading ? 'Loading…' : total === 0 ? 'No rows' : <><b className="text-[color:var(--oa-text)]">{count(from)}–{count(to)}</b> of <b className="text-[color:var(--oa-text)]">{count(total)}</b></>}
          {selectedCount ? <span className="ml-2 text-[color:var(--oa-accent)]">· {count(selectedCount)} selected</span> : null}
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-1.5">
            Rows
            <select value={pageSize} onChange={(e) => table.setPageSize(Number(e.target.value))} className="h-7 rounded-md border border-[color:var(--oa-line-2)] bg-[color:var(--oa-card)] px-1.5 text-[12px] text-[color:var(--oa-text)] outline-none focus:border-[color:var(--oa-accent)]">
              {[50, 100, 200, 500].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
          <div className="flex items-center gap-1">
            {[
              { icon: ChevronsLeft, act: () => table.setPageIndex(0), ok: table.getCanPreviousPage(), label: 'First page' },
              { icon: ChevronLeft, act: () => table.previousPage(), ok: table.getCanPreviousPage(), label: 'Previous page' },
            ].map(({ icon: Icon, act, ok, label }) => (
              <button key={label} type="button" onClick={act} disabled={!ok} aria-label={label} className="rounded-md border border-[color:var(--oa-line-2)] p-1 text-[color:var(--oa-text)] hover:bg-[color:var(--oa-raised)] disabled:opacity-40 disabled:hover:bg-[color:var(--oa-card)]"><Icon className="h-4 w-4" /></button>
            ))}
            <span className="px-2 tabular-nums">Page <b className="text-[color:var(--oa-text)]">{pageIndex + 1}</b> of {Math.max(1, table.getPageCount())}</span>
            {[
              { icon: ChevronRight, act: () => table.nextPage(), ok: table.getCanNextPage(), label: 'Next page' },
              { icon: ChevronsRight, act: () => table.setPageIndex(table.getPageCount() - 1), ok: table.getCanNextPage(), label: 'Last page' },
            ].map(({ icon: Icon, act, ok, label }) => (
              <button key={label} type="button" onClick={act} disabled={!ok} aria-label={label} className="rounded-md border border-[color:var(--oa-line-2)] p-1 text-[color:var(--oa-text)] hover:bg-[color:var(--oa-raised)] disabled:opacity-40 disabled:hover:bg-[color:var(--oa-card)]"><Icon className="h-4 w-4" /></button>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};
